import { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Collapse } from '@mui/material';
import { round, point } from '@turf/helpers';
import { lineArc } from '@turf/line-arc';
import buffer from '@turf/buffer';
import { isEqual, isNumber, throttle } from 'lodash-es';
import { NumericTextField } from '../../components/NumericTextField';
import { usePrevious } from '../../hooks/use-previous';

IrrigationEditControls.propTypes = {
  map: PropTypes.object,
  boundary: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    angle: PropTypes.number,
    heading: PropTypes.number,
    radius: PropTypes.number,
    shape: PropTypes.object,
  }).isRequired,
  setBoundary: PropTypes.func.isRequired,
};

export function IrrigationEditControls(props) {
  const { map, boundary, setBoundary } = props;
  const marker = useRef();
  const polygon = useRef();
  const previousBoundary = usePrevious(boundary);

  const updateBoundary = useCallback(
    (changes) => {
      setBoundary((current) => ({
        ...current,
        x: isNumber(changes.x) ? round(changes.x, 6) : current.x,
        y: isNumber(changes.y) ? round(changes.y, 6) : current.y,
        angle: isNumber(changes.angle) ? changes.angle : current.angle,
        heading: isNumber(changes.heading) ? changes.heading : current.heading,
        radius: isNumber(changes.radius) ? changes.radius : current.radius,
        shape: changes.shape || current.shape,
      }));
    },
    [setBoundary],
  );

  useEffect(() => {
    if (!boundary.x)
      updateBoundary({
        x: map.center.lng(),
        y: map.center.lat(),
        radius: 0,
        heading: 0,
        angle: 360,
      });
  }, [boundary, map, updateBoundary]);

  const shouldSyncBoundary = useMemo(() => {
    const attributes = ['x', 'y', 'angle', 'heading', 'radius'];
    const isValid = attributes.every((attr) => isNumber(boundary[attr]));
    const hasChanged = !isEqual(boundary, previousBoundary);
    return isValid && hasChanged;
  }, [boundary, previousBoundary]);

  useEffect(syncMount, [map]);
  useEffect(syncMarker, [map, boundary, updateBoundary, shouldSyncBoundary]);
  useEffect(syncPolygon, [map, boundary, updateBoundary, shouldSyncBoundary]);

  if (boundary.x === undefined) return null;

  return (
    <Collapse in>
      <Box
        data-testid="irrigation-editor-controls"
        sx={{
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          bgcolor: 'white',
          color: '#666',
          height: 55,
          px: -0.5,
          '& button:hover': { color: '#333' },
          '& button': { borderRadius: 0, mt: 0, width: 65 },
          '& button.active': {
            color: 'white',
            bgcolor: (theme) => theme.palette.primary.main,
          },
        }}
      >
        {buildTextField('x', { allowDecimals: true, min: -180, max: 180 })}
        {buildTextField('y', { allowDecimals: true, min: -90, max: 90 })}
        {buildTextField('radius', {
          type: 'number',
          label: 'radius (ft)',
          width: 90,
          min: 0,
          max: 2640,
        })}
        {buildTextField('angle', {
          type: 'number',
          width: 90,
          min: 1,
          max: 360,
        })}
        {buildTextField('heading', {
          type: 'number',
          width: 90,
          min: 0,
          max: 360,
          disabled: boundary.angle === 360,
        })}
      </Box>
    </Collapse>
  );

  function buildTextField(attribute, config = {}) {
    const { width = 105, label = attribute, ...passThroughProps } = config;
    return (
      <NumericTextField
        label={label}
        value={boundary[attribute]}
        onChange={(e) => updateBoundary({ [attribute]: e.target.value })}
        size="small"
        sx={{
          mx: 1,
          width,
          '& input': { px: 1.25, py: 0.75 },
          '& .MuiFormHelperText-root': { display: 'none' },
        }}
        InputLabelProps={{ shrink: true }}
        {...passThroughProps}
      />
    );
  }

  function syncMount() {
    map.getDiv()?.classList?.add('editing');
  }

  function syncMarker() {
    if (!shouldSyncBoundary) return;

    if (!marker.current) {
      marker.current = new window.google.maps.Marker({
        position: { lat: boundary.y, lng: boundary.x },
        draggable: true,
        map,
      });

      window.google.maps.event.addListener(
        marker.current,
        'dragend',
        (event) => {
          updateBoundary({ x: event.latLng.lng(), y: event.latLng.lat() });
        },
      );

      window.google.maps.event.addListener(
        marker.current,
        'drag',
        throttle((event) => {
          updateBoundary({ x: event.latLng.lng(), y: event.latLng.lat() });
        }, 50),
      );
    }

    const centroid = new window.google.maps.LatLng(boundary.y, boundary.x);
    marker.current.setPosition(centroid);

    if (!map.getBounds().contains(centroid)) {
      map.setCenter(centroid);
    }
  }

  function syncPolygon() {
    if (!shouldSyncBoundary || !boundary.radius) return;

    const circle = buildCirclePolygon(boundary);

    if (!polygon.current) {
      polygon.current = new window.google.maps.Polygon({
        map,
        fillColor: '#FFFF00',
        strokeColor: '#FFFF00',
      });
    }

    polygon.current.setPaths(
      circle.geometry.coordinates[0].map(([lng, lat]) => ({ lat, lng })),
    );

    updateBoundary({ shape: circle.geometry });
  }
}

function buildCirclePolygon({ x, y, heading, angle, radius }) {
  const centroid = point([x, y]);
  const options = { steps: 100, units: 'feet' };

  if (angle === 360) {
    return buffer(centroid, radius, options);
  }

  const arc = lineArc(centroid, radius, heading, heading + angle, options);
  return {
    geometry: {
      type: 'Polygon',
      coordinates: [[[x, y], ...arc.geometry.coordinates, [x, y]]],
    },
  };
}
